import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Paper, Typography, Card, CardHeader, CardContent, Chip, Divider, Box, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { API_PATHS } from '../../../utils/constants';
import { getToken } from '../../../services/auth';
import { fetchDocumentsSummary, fetchMissingDocumentsSummary } from '../../../services/reports';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import { Bar, Pie, Line } from 'react-chartjs-2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import WarningIcon from '@mui/icons-material/Warning';
import AppsIcon from '@mui/icons-material/Apps';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutorenewIcon from '@mui/icons-material/Autorenew';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  ChartTitle,
  ChartTooltip,
  ChartLegend
);

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#F5F5F5',
  minHeight: '100vh',
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
}));

const DataTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: '0.5rem 1rem',
    height: '32px',
    fontSize: '0.875rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#064d51',
    color: '#ffffff',
    fontWeight: 600,
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: 'rgba(192, 242, 243, 0.1)',
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: '#064d51',
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const TrendIndicator = styled(Box)(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  color: trend === 'up' ? '#27ae60' : '#e74c3c',
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

// Use similar semantic coloring as Recent Activity for action chips
const nextActionColor = (action) => {
  const s = String(action || '').toLowerCase();
  if (s.includes('payment')) return '#e74c3c';       // red
  if (s.includes('upload')) return '#f39c12';        // amber
  if (s.includes('approve')) return '#27ae60';       // green (future-proof)
  if (s.includes('medical') || s.includes('visit')) return '#3498db'; // blue
  if (s.includes('document')) return '#064d51';      // teal default
  return '#064d51';
};

// Small renderer for status icons in Next Actions
const renderActionStatusIcon = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'IN_PROGRESS') {
    return (
      <Tooltip title="IN_PROGRESS" arrow>
        <AutorenewIcon fontSize="small" sx={{ color: '#f39c12' }} />
      </Tooltip>
    );
  }
  if (s === 'PENDING') {
    return (
      <Tooltip title="PENDING" arrow>
        <HourglassEmptyIcon fontSize="small" sx={{ color: '#064d51' }} />
      </Tooltip>
    );
  }
  return null;
};

// Ticket Status pie — real distribution from the API. Map each status to a
// stable colour and a human label (every status the backend returns is shown).
const TICKET_STATUS_COLORS = {
  OPEN: '#3498db',
  NEW: '#2980b9',
  IN_PROGRESS: '#f39c12',
  ASSIGNED: '#9b59b6',
  ASSIGNED_TO_ORGANIZATION: '#8e44ad',
  TAKEN: '#1abc9c',
  COMPLETED: '#27ae60',
  CLOSED: '#7f8c8d',
  REJECTED: '#e74c3c',
  INCOMPLETE: '#e67e22',
  MODIFICATION_REQUIRED: '#d35400',
};
const statusColor = (status) => TICKET_STATUS_COLORS[String(status || '').toUpperCase()] || '#064d51';
const statusLabel = (status) => String(status || '')
  .replace(/_/g, ' ')
  .toLowerCase()
  .replace(/\b\w/g, (c) => c.toUpperCase());

// Palette for the Top Professions doughnut
const PROFESSION_COLORS = ['#064d51', '#0a7c82', '#3498db', '#9b59b6', '#27ae60', '#f39c12', '#e67e22', '#7f8c8d'];

// Dashboard component
const CustomerDashboard = () => {
  const [expiringDocs, setExpiringDocs] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showAllRecent, setShowAllRecent] = useState(false);
  const [showAllExpiring, setShowAllExpiring] = useState(false);
  const [summary, setSummary] = useState(null);
  const [nextActions, setNextActions] = useState([]);
  const [showAllActions, setShowAllActions] = useState(false);
  const [ticketStatusCounts, setTicketStatusCounts] = useState({});
  const [trends, setTrends] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [docHealth, setDocHealth] = useState(null);
  const [processingTime, setProcessingTime] = useState([]);
  const [loading, setLoading] = useState(false);
  const [org, setOrg] = useState(null);
  const token = useMemo(() => getToken(), []);

  const { from, to } = useMemo(() => {
    const today = dayjs();
    return { from: today.toISOString(), to: today.add(60, 'day').toISOString() };
  }, []);

  const safeFetchJson = async (url, token, fallback = null) => {
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return fallback;
      return await res.json();
    } catch (e) {
      return fallback;
    }
  };

  useEffect(() => {
    let mounted = true;
    if (!token) return;
    const q = new URLSearchParams();
    q.set('from', from);
    q.set('to', to);
    const load = async () => {
      setLoading(true);
      // Fire every dashboard request in parallel for faster loading. Each call is
      // independently fault-tolerant (safeFetchJson swallows errors; the summary
      // calls have .catch) so one failure can't blank the whole page.
      // Org is inferred from the token by each endpoint.
      const [agg, actions, profs, processing, docSummary, missingSummary] = await Promise.all([
        safeFetchJson(`${API_PATHS.V1}/dashboard/document-services/aggregate?${q.toString()}`, token, null),
        safeFetchJson(`${API_PATHS.V1}/ticket/actions/pending`, token, []),
        safeFetchJson(`${API_PATHS.V1}/dashboard/document-services/professions?limit=8`, token, []),
        safeFetchJson(`${API_PATHS.V1}/dashboard/document-services/processing-time?limit=8`, token, []),
        fetchDocumentsSummary({ days: 60, isCurrent: true }).then((r) => r.data).catch(() => null),
        fetchMissingDocumentsSummary({}).then((r) => r.data).catch(() => null),
      ]);
      if (!mounted) return;
      if (agg) {
        setExpiringDocs(Array.isArray(agg.expiringDocuments) ? agg.expiringDocuments : []);
        setRecentActivity(Array.isArray(agg.recentActivity) ? agg.recentActivity : []);
        setSummary(agg.summary || null);
        setTicketStatusCounts(agg.ticketStatusCounts || {});
        setTrends(Array.isArray(agg.trends) ? agg.trends : []);
      }
      setNextActions(Array.isArray(actions) ? actions : []);
      setProfessions(Array.isArray(profs) ? profs : []);
      setProcessingTime(Array.isArray(processing) ? processing : []);
      // Document health: missing vs expiring/expired counts from the report summaries.
      const expired = Number(docSummary?.alreadyExpired || 0);
      const totalExpiring = Number(docSummary?.totalExpiring || 0);
      setDocHealth({
        missing: Number(missingSummary?.totalMissingDocuments || 0),
        expiringSoon: Math.max(0, totalExpiring - expired),
        expired,
      });
      setLoading(false);
    };
    load();
    return () => { mounted = false; };
  }, [token, from, to]);

  // Ticket Status pie — built from the real status->count map (every status).
  const ticketStatusChartConfig = useMemo(() => {
    const entries = Object.entries(ticketStatusCounts || {});
    return {
      labels: entries.map(([status]) => statusLabel(status)),
      datasets: [{
        data: entries.map(([, count]) => count),
        backgroundColor: entries.map(([status]) => statusColor(status)),
        borderWidth: 0,
      }],
    };
  }, [ticketStatusCounts]);

  // Monthly Request Volume — daily ticket inflow over the last 30 days.
  const monthlyRequestVolume = useMemo(() => ({
    labels: trends.map((t) => dayjs(t.date).format('MMM D')),
    datasets: [{
      label: 'Document Requests',
      data: trends.map((t) => t.created),
      borderColor: '#064d51',
      backgroundColor: 'rgba(6, 77, 81, 0.1)',
      tension: 0.4,
      fill: true,
    }],
  }), [trends]);

  // Top Professions — employee headcount by profession (separate API).
  const professionChart = useMemo(() => ({
    labels: professions.map((p) => p.profession),
    datasets: [{
      data: professions.map((p) => p.count),
      backgroundColor: professions.map((_, i) => PROFESSION_COLORS[i % PROFESSION_COLORS.length]),
      borderWidth: 0,
    }],
  }), [professions]);
  const professionTotal = useMemo(
    () => professions.reduce((acc, p) => acc + (p.count || 0), 0),
    [professions],
  );

  // Missing & Expiring Documents — counts pulled from the report summaries.
  const docHealthChart = useMemo(() => {
    const m = docHealth || { missing: 0, expiringSoon: 0, expired: 0 };
    return {
      labels: ['Missing', 'Expiring Soon', 'Expired'],
      datasets: [{
        data: [m.missing || 0, m.expiringSoon || 0, m.expired || 0],
        backgroundColor: ['#e67e22', '#f39c12', '#e74c3c'],
        borderWidth: 0,
      }],
    };
  }, [docHealth]);
  const docHealthTotal = useMemo(() => {
    const m = docHealth || {};
    return (m.missing || 0) + (m.expiringSoon || 0) + (m.expired || 0);
  }, [docHealth]);

  // Processing Time by Service — average days from creation to completion.
  const processingTimeChart = useMemo(() => ({
    labels: processingTime.map((p) => p.service),
    datasets: [{
      label: 'Avg Days',
      data: processingTime.map((p) => p.avgDays),
      backgroundColor: '#064d51',
    }],
  }), [processingTime]);

  return (
    <DashboardContainer>
      {/* DashboardSelector removed per design request */}
      <Typography variant="h5" sx={{ my: 2, color: '#064d51', fontWeight: 600 }}>
        Document Services Dashboard
      </Typography>
      
      {/* Top Section - Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{summary?.activeRequests?.count ?? summary?.activeRequestsCount ?? 0}</MetricValue>
            <MetricLabel>Active Requests</MetricLabel>
            {(() => {
              // delta is weekly inflow (requests created this week vs last):
              // more requests = up/green, fewer = down/red.
              const delta = summary?.activeRequests?.delta ?? 0;
              const trend = delta < 0 ? 'down' : 'up';
              const phrase = delta === 0 ? 'same as last week' : `${Math.abs(delta)} ${delta > 0 ? 'more' : 'less'} than last week`;
              return (
                <TrendIndicator trend={trend}>
                  {delta < 0 ? (
                    <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  ) : (
                    <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  )}
                  <Typography variant="caption">{phrase}</Typography>
                </TrendIndicator>
              );
            })()}
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{summary?.documentComplianceRate ?? 0}%</MetricValue>
            <MetricLabel>Document Compliance Rate</MetricLabel>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {`${summary?.validDocuments ?? 0} of ${summary?.requiredDocuments ?? 0} documents valid`}
            </Typography>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{summary?.activeEmployeesCount ?? 0}</MetricValue>
            <MetricLabel>Active Employees</MetricLabel>
            {(() => {
              const delta = summary?.activeEmployees?.delta ?? 0;
              const trend = delta > 0 ? 'up' : (delta < 0 ? 'down' : 'up');
              const phrase = delta === 0 ? 'same as last month' : `${Math.abs(delta)} ${delta > 0 ? 'more' : 'less'} than last month`;
              return (
                <TrendIndicator trend={trend}>
                  {delta > 0 ? (
                    <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  ) : delta < 0 ? (
                    <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  ) : (
                    <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  )}
                  <Typography variant="caption">{phrase}</Typography>
                </TrendIndicator>
              );
            })()}
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MetricValue>{summary?.expiringSoonCount ?? 0}</MetricValue>
              <WarningIcon sx={{ color: '#f39c12', ml: 1 }} />
            </Box>
            <MetricLabel>Documents Expiring Soon</MetricLabel>
            {(() => {
              const delta = summary?.expiringSoon?.delta ?? summary?.delta ?? 0;
              const trend = delta < 0 ? 'up' : 'down'; // fewer expiring docs is good (green)
              const phrase = delta === 0 ? 'same as last week' : `${Math.abs(delta)} ${delta < 0 ? 'less' : 'more'} than last week`;
              return (
                <TrendIndicator trend={trend}>
                  {delta < 0 ? (
                    <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
                  )}
                  <Typography variant="caption">{phrase}</Typography>
                </TrendIndicator>
              );
            })()}
          </MetricCard>
        </Grid>
      </Grid>
      
      {/* Middle Section - Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartCard>
            <CardHeader
              title="Monthly Request Volume"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Line
                data={monthlyRequestVolume}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardHeader
              title="Ticket Status"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Pie
                data={ticketStatusChartConfig}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        padding: 10,
                      },
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Top Professions"
              subheader={`Total: ${professionTotal}`}
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {professions.length > 0 ? (
                <Pie
                  data={professionChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '55%',
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          boxWidth: 12,
                          padding: 10,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed || 0;
                            const pct = professionTotal ? ((value / professionTotal) * 100).toFixed(1) : '0.0';
                            return `${context.label} — ${value} (${pct}%)`;
                          }
                        }
                      }
                    }
                  }}
                  height={220}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
                  No profession data
                </Typography>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Missing & Expiring Documents"
              subheader={`Total: ${docHealthTotal}`}
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {docHealthTotal > 0 ? (
                <Pie
                  data={docHealthChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right',
                        labels: {
                          boxWidth: 12,
                          padding: 10,
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const value = context.parsed || 0;
                            const pct = docHealthTotal ? ((value / docHealthTotal) * 100).toFixed(1) : '0.0';
                            return `${context.label} — ${value} (${pct}%)`;
                          }
                        }
                      }
                    },
                  }}
                  height={180}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No missing or expiring documents
                </Typography>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Processing Time by Service"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {processingTime.length > 0 ? (
                <Bar
                  data={processingTimeChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const days = context.parsed.y ?? 0;
                            const cnt = processingTime[context.dataIndex]?.count ?? 0;
                            return `${days} day(s) avg — ${cnt} ticket(s)`;
                          }
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: 'Days',
                        },
                      },
                    },
                  }}
                  height={180}
                />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  No processing-time data
                </Typography>
              )}
            </CardContent>
          </ChartCard>
        </Grid>

        {/* Next Actions moved here to fill space next to Processing Time */}
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Next Actions"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              action={(() => {
                const total = (nextActions && nextActions.length) || 0;
                const canToggle = total > 6;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canToggle && (
                      <Tooltip title={showAllActions ? 'Show Less' : `View All (${total - 6})`} arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setShowAllActions(v => !v)}
                          sx={{ border: '1px solid rgba(25,118,210,0.35)', bgcolor: 'rgba(25,118,210,0.08)', '&:hover': { bgcolor: 'rgba(25,118,210,0.16)' } }}
                        >
                          {showAllActions ? <CloseFullscreenIcon fontSize="small" /> : <AppsIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })()}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Due Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nextActions && nextActions.length > 0 ? (
                      nextActions.slice(0, showAllActions ? undefined : 6).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <Tooltip title={r.employeeId ? `ID: ${r.employeeId}` : ''} arrow>
                              <span>{r.employeeName}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={r.action}
                              size="small"
                              sx={{
                                bgcolor: nextActionColor(r.action),
                                color: '#fff',
                                fontWeight: 500,
                                height: 22,
                                borderRadius: '12px',
                                px: 1.2
                              }}
                            />
                          </TableCell>
                          <TableCell>{renderActionStatusIcon(r.status)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.dueDate ? dayjs(r.dueDate).isValid() ? dayjs(r.dueDate).format('YYYY-MM-DD') : r.dueDate : ''}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No pending actions
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
      
      {/* Bottom Section - Tables */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Documents Expiring Soon"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              action={(() => {
                const total = expiringDocs.length || 0;
                const canToggle = total > 6;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canToggle && (
                      <Tooltip title={showAllExpiring ? 'Show Less' : `View All (${total - 6})`} arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setShowAllExpiring(v => !v)}
                          sx={{ border: '1px solid rgba(25,118,210,0.35)', bgcolor: 'rgba(25,118,210,0.08)', '&:hover': { bgcolor: 'rgba(25,118,210,0.16)' } }}
                        >
                          {showAllExpiring ? <CloseFullscreenIcon fontSize="small" /> : <AppsIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })()}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Employee</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Document</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>Expiry Date</TableCell>
                      <TableCell align="right">Days Left</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expiringDocs
                      .slice(0, showAllExpiring ? undefined : 6)
                      .map((doc, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {doc.employeeName || `${doc.firstName || ''} ${doc.lastName || ''}`.trim()}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {doc.documentType}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {doc.expiryDate}
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={doc.daysRemaining}
                            size="small"
                            sx={{ 
                              backgroundColor: doc.daysRemaining <= 7 ? '#e74c3c' : 
                                              doc.daysRemaining <= 14 ? '#f39c12' : '#27ae60',
                              color: 'white',
                              fontWeight: 500,
                              minWidth: '32px'
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Recent Activity"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              action={(() => {
                const total = recentActivity.length || 0;
                const canToggle = total > 6;
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {canToggle && (
                      <Tooltip title={showAllRecent ? 'Show Less' : `View All (${total - 6})`} arrow>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => setShowAllRecent(v => !v)}
                          sx={{ border: '1px solid rgba(25,118,210,0.35)', bgcolor: 'rgba(25,118,210,0.08)', '&:hover': { bgcolor: 'rgba(25,118,210,0.16)' } }}
                        >
                          {showAllRecent ? <CloseFullscreenIcon fontSize="small" /> : <AppsIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                );
              })()}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentActivity
                      .slice(0, showAllRecent ? undefined : 6)
                      .map((activity, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            const t = activity.time || activity.timestamp;
                            const parsed = typeof t === 'string' ? t.replace(' ', 'T') : t;
                            const formatted = parsed ? dayjs(parsed).isValid() ? dayjs(parsed).format('YYYY-MM-DD HH:mm') : (activity.time || activity.timestamp) : '';
                            return formatted;
                          })()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            {activity.type ? (
                              <Chip
                                label={(activity.type || '').toUpperCase()}
                                size="small"
                                sx={{
                                  bgcolor: (activity.type || '').toUpperCase() === 'WF' ? '#27ae60' : '#0a7c82',
                                  color: '#fff',
                                  height: 22,
                                  borderRadius: '12px',
                                }}
                              />
                            ) : null}
                            <Tooltip title={activity.action || ''} arrow>
                              <Typography variant="body2" sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.875rem' }}>
                                {(activity.action || '').slice(0, 18)}{(activity.action || '').length > 18 ? '…' : ''}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activity.employeeName || activity.user || ''}</TableCell>
                        <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activity.details || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>

      </Grid>
    </DashboardContainer>
  );
};

export default CustomerDashboard;
